import { Component, Input, OnInit, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs/internal/Subscription';
import { Game, UserProfile } from 'src/app/models';
import { GameCategoryList, GameList } from 'src/app/models/game.interface';
import {
	AuthService,
	ThemeService,
	TokenService,
	HttpService,
	ScreenSizeService,
} from 'src/app/services';
import { environment as env } from 'src/environments/environment';
import { Router } from '@angular/router';

@Component({
	selector: 'cm-games',
	templateUrl: './games.component.html',
	styleUrls: ['./games.component.css']
})
export class GamesComponent implements OnInit, AfterViewInit {
	@ViewChildren('scrollableDivs') scrollableDivs!: QueryList<ElementRef>;

	@Input() currency: string;
	@Input() playerId: string;
	@Input() cash: number;
	@Input() inGameId: string;

	protected gameCategoryList: GameCategoryList | null;
	protected gameList: GameList | null;
	protected games: Game[];
	protected languageFolderName: string;
	protected selectedGameType: string;

	protected language: string | null;
	protected currentTheme: string;
	private theme$: Subscription | null;
	private language$: Subscription | null;
	showButtons: boolean[] = [];
	private userProfile: UserProfile | null;

	// SE - Boo 
	// flag to control redirect to game-lobby or loading-page
	// if provider code includes here will redirect to game-lobby
	protected providerWithCustomLobby: string[] = ['GMPGS', 'GMPPS', 'GMJLS', 'GMSPG'];

	constructor(
		private token: TokenService,
		public auth: AuthService,
		private translate: TranslateService,
		private theme: ThemeService,
		private http: HttpService,
		private screenSizeService: ScreenSizeService,
		protected router: Router
	) {
		this.gameCategoryList = null;
		this.gameList = null;
		this.games = [];
		this.currency = '';
		this.playerId = '';
		this.cash = 0;
		this.inGameId = '';
		this.languageFolderName = 'EN_US';
		this.selectedGameType = 'Casino';

		this.language = '';
		this.currentTheme = this.theme.getTheme();
		this.theme$ = null;
		this.language$ = null;

		this.userProfile = null;
	}

	async ngOnInit() {
		this.language = this.token.getLocalToken(env.TokenKeys.LanguageToken);

		this.language$ = this.translate.onLangChange.subscribe((value: any) => {
			this.language = value.lang;

			if (this.language)
				this.languageFolderName = this.getCapitalUnderscoreLanguageCode(
					this.language
				);

			if (this.gameCategoryList) {
				this.gameCategoryList.List.forEach((category) => {
					category.DisplayName = this.getGameNameInDiffLanguage(category.ProductCode.toLowerCase());
				});
			}
		});

		if (this.language)
			this.languageFolderName = this.getCapitalUnderscoreLanguageCode(
				this.language
			);

		if (this.gameCategoryList) {
			this.gameCategoryList.List.forEach((category) => {
				category.DisplayName = this.getGameNameInDiffLanguage(category.ProductCode.toLowerCase());
			});
		}

		this.userProfile = this.auth.getUserProfile();

		const gt: string | null = this.token.getSessionToken(
			env.TokenKeys.SelectedGameType
		);
		if (gt) {
			this.selectedGameType = gt;
			this.token.removeSessionToken(env.TokenKeys.SelectedGameType);
		} else {
			this.selectedGameType = 'Casino';
		}

		this.subscribeToThemeChange();
		await this.getProductList();
		await this.getGameList();
	}

	ngAfterViewInit(): void {
		this.scrollableDivs.changes.subscribe(() => {
			this.scrollableDivs.forEach((_, index) => {
				this.checkScroll(index);
			});
		});

		this.screenSizeService.getScreenWidth().subscribe(() => {
			this.scrollableDivs.forEach((_, index) => {
				this.checkScroll(index);
			});
		});

		this.scrollableDivs.forEach((_, index) => {
			this.checkScroll(index);
		});
	}

	ngOnDestroy(): void {
		this.theme$?.unsubscribe();
		this.language$?.unsubscribe();
	}

	protected joinGame() {
		if (!this.auth.isAuthenticated()) {
			this.auth.toggleLoginDisplay(!this.auth.isAuthenticated());
			return;
		}
	}

	protected handleGameList(gameType: string) {
		this.selectedGameType = gameType;
	}

	private subscribeToThemeChange(): void {
		this.theme$ = this.theme._thememode$.subscribe((theme) => {
			this.currentTheme = theme;
		});
	}

	protected getProductList() {

		return new Promise(async (resolve) => {
			const payload: any = {
				CurrentCulture: this.language,
				// AcctUserId: string,
				// UserRoleId: 0,
				// LoggedInAcctUserId: string,
				IpAddress: this.userProfile?.IpAddress,
				// UserAgent: string,
				// Country: string
			};
			this.http
				.post('provider', 'getproductlist', payload)
				.subscribe((resp: GameCategoryList) => {
					if (resp && resp.Code == 1) {
						this.gameCategoryList = resp;

						this.gameCategoryList.List = this.gameCategoryList.List.filter((category) => {
							category.Name = this.capitalizeFirstLetter(category.ProductCode);
							category.DisplayName = this.getGameNameInDiffLanguage(category.ProductCode.toLowerCase());

							if (category.ProductCode.toLowerCase() === 'casino') {
								category.Icon = 'tab-icon-01.png';
								return true; 
							} else if (category.ProductCode.toLowerCase() == 'slots') {
								category.Icon = 'tab-icon-05.png';
								return true;
							} else if (category.ProductCode.toLowerCase() == 'fish') {
								category.Icon = 'tab-icon-02.png';
								return true;
							} else if (category.ProductCode.toLowerCase() == 'sports') {
								category.Icon = 'tab-icon-04.png';
								return true;
							} else if (category.ProductCode.toLowerCase() == 'chess') {
								category.Icon = 'tab-icon-03.png';
								return true;
							} 

							return false; // Exclude all other categories
						});
						
						resolve(true);
					} else {
						resolve(false);
					}
				});
		});
	}

	private getGameNameInDiffLanguage(code: string): string {
		// temporary hardcode game name in different language
		const nameList = [
			{
				Code: "Casino",
				Name: "Casino",
				NameCn: "赌场",
				NameTh: "วีดีโอ",
				NameId: "Kasino",
				NameVn: "Băng hình",
				NameKo: "동영상",
				NameTw: "賭場",
				NameJp: "ビデオ"
			},			
			{
				Code: "Slots",
				Name: "Slots",
				NameCn: "老虎机",
				NameTh: "หมากรุกและไพ่",
				NameId: "Mesin Slot",
				NameVn: "điện tử",
				NameKo: "전자",
				NameTw: "老虎機",
				NameJp: "スロット"
			},
			{
				Code: "Sports",
				Name: "Sports",
				NameCn: "体育",
				NameTh: "พลศึกษา",
				NameId: "Olahraga",
				NameVn: "giáo dục thể chất",
				NameKo: "체육",
				NameTw: "體育",
				NameJp: "スポーツ"
			},
			{
				Code: "Chess",
				Name: "Chess",
				NameCn: "棋牌",
				NameTh: "หมากรุกและไพ่",
				NameId: "Catur",
				NameVn: "cờ vua và bài",
				NameKo: "체스와 카드",
				NameTw: "棋牌",
				NameJp: "チェスとカード"
			},
			{
				Code: "Fish",
				Name: "Fish",
				NameCn: "捕鱼",
				NameTh: "หมากรุกและไพ่",
				NameId: "Perikanan",
				NameVn: "đánh bắt cá",
				NameKo: "어업",
				NameTw: "捕魚",
				NameJp: "釣り"
			}
		];
		let name = '';

		nameList.forEach(val => {
			if (val.Code.toLowerCase() == code) {
				if (this.language == 'en-US') {
					name = val.Name;
				} else if (this.language == 'zh-CN') {
					name = val.NameCn;
				} else if (this.language == 'id-ID') {
					name = val.NameId;
				} else if (this.language == 'vi-VN') {
					name = val.NameVn;
				} else if (this.language == 'th-TH') {
					name = val.NameTh;
				} else if (this.language == 'jp-JP') {
					name = val.NameJp;
				} else if (this.language == 'ko-KO') {
					name = val.NameKo;
				} else if (this.language == 'tw-TW') {
					name = val.NameTw;
				}
			}
		});

		return name;
	}

	private capitalizeFirstLetter(str: string): string {
		if (!str) return str; // Handle empty string case
		return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
	}

	protected getGameList() {
		return new Promise(async (resolve) => {
			const payload: any = {
				CurrentCulture: this.language,
				// AcctUserId: null,
				// UserRoleId: 0,
				// LoggedInAcctUserId: null,
				IpAddress: this.userProfile?.IpAddress,
				// UserAgent: null,
				// Country: null,
				// Search: "",
				// PageNumber: 1,
				// PageSize: 9999,
				// SortBy: 1,
				// SortDirection: 0,
				ProviderId: null,
				GameCategoryId: null,
				Status: true
			};

			this.http
				.post('provider', 'gamelist', payload)
				.subscribe((resp: GameList) => {
					if (resp && resp.Code == 1) {
						this.gameList = resp;

						if (this.gameList?.Records.length > 0) {
							this.gameList.Records.forEach((record) => {
								record.ImageSource = this.getImageSource(record);

								if (this.gameCategoryList) {
									this.gameCategoryList.List.forEach((category) => {
										if (
											category.Name.toLowerCase() ==
											record.GameCategoryName.toLowerCase()
										) {

											if (record.DisplayCategory != null) {
												var displayCategoryArr = record.DisplayCategory.split(',');
												// console.log(displayCategoryArr);

												displayCategoryArr.forEach(dc => {
													if (dc == 'supplier') {
														if (!category.SupplierGames) {
															category.SupplierGames = []; // Ensure HotGames is initialized
														}

														category.SupplierGames.push(record);
														category.SupplierGames.sort((a, b) => a.Seq - b.Seq);
													} else if (dc == 'hot') {
														if (!category.HotGames) {
															category.HotGames = []; // Ensure HotGames is initialized
														}

														category.HotGames.push(record);
														category.HotGames.sort((a, b) => a.Seq - b.Seq);
													}
												});
											}

											// if (!record.GameName.includes('LOBBY')) {
											// 	if (!category.HotGames) {
											// 		category.HotGames = []; // Ensure HotGames is initialized
											// 	}

											// 	if (!this.providerWithCustomLobby.includes(record.ProviderCode)) { // Game with custom lobby dont show in landing page
											// 		category.HotGames.push(record);
											// 	}
											// } else {
											// 	if (!category.SupplierGames) {
											// 		category.SupplierGames = []; // Ensure SupplierGames is initialized
											// 	}
											// 	category.SupplierGames.push(record);
											// }
										}
									});
								}
							});
						}
						resolve(true);
					} else {
						resolve(false);
					}
				});
		});
	}

	private getCapitalUnderscoreLanguageCode(locale: string): string {
		return locale
			.split('-')
			.map((part) => part.toUpperCase())
			.join('_');
	}

	protected getImageSource(model: any): string {
		let imageSource = model?.Img_EN_US;

		if (this.language == 'en-US') {
			imageSource = model?.Img_EN_US;
		}
		else if (this.language == 'id-ID') {
			imageSource = model?.Img_ID_ID;
		}
		else if (this.language == 'jp-JP') {
			imageSource = model?.Img_JP_JP;
		}
		else if (this.language == 'ko-KO') {
			imageSource = model?.Img_KO_KO;
		}
		else if (this.language == 'th-TH') {
			imageSource = model?.Img_TH_TH;
		}
		else if (this.language == 'tw-TW') {
			imageSource = model?.Img_TW_TW;
		}
		else if (this.language == 'vi-VN') {
			imageSource = model?.Img_VI_VN;
		}
		else if (this.language == 'zh-CN') {
			imageSource = model?.Img_ZH_CN;
		}

		return `${env.AdminAPIURL}/uploads/provider_game/${imageSource}`;;
	}

	// scrollLeft(index: number) {
	//   const scrollableDiv = this.scrollableDivs.toArray()[index];
	//   scrollableDiv.nativeElement.scrollBy({ left: -150, behavior: 'smooth' });
	// }

	// scrollRight(index: number) {
	//   const scrollableDiv = this.scrollableDivs.toArray()[index];
	//   scrollableDiv.nativeElement.scrollBy({ left: 150, behavior: 'smooth' });
	// }

	// checkScroll(index: number) {
	//   const scrollableDiv = this.scrollableDivs.toArray()[index].nativeElement;
	//   const overflowX = scrollableDiv.scrollWidth > scrollableDiv.clientWidth;
	//   this.showButtons[index] = overflowX;
	// }


	scrollLeft(index: number) {
		const scrollableDiv = this.scrollableDivs.toArray()[index].nativeElement;
		const newScrollLeft = scrollableDiv.scrollLeft - 150;
		scrollableDiv.scrollBy({ left: -150, behavior: 'smooth' });

		// Prevent scrolling past the start
		if (newScrollLeft <= 0) {
			scrollableDiv.scrollLeft = 0;
		}

		this.checkScroll(index);
	}

	scrollRight(index: number) {
		const scrollableDiv = this.scrollableDivs.toArray()[index].nativeElement;
		const newScrollLeft = scrollableDiv.scrollLeft + 150;
		const maxScrollLeft = scrollableDiv.scrollWidth - scrollableDiv.clientWidth;
		scrollableDiv.scrollBy({ left: 150, behavior: 'smooth' });

		// Prevent scrolling past the end
		if (newScrollLeft >= maxScrollLeft) {
			scrollableDiv.scrollLeft = maxScrollLeft;
		}

		this.checkScroll(index);
	}

	checkScroll(index: number) {
		const scrollableDiv = this.scrollableDivs.toArray()[index].nativeElement;
		const overflowX = scrollableDiv.scrollWidth > scrollableDiv.clientWidth;
		this.showButtons[index] = overflowX;
	}

	protected getSupplierDisplayName(game: any): string {
		return game[`Display_${this.languageFolderName}`];
	}

	public enterGame(game: Game, isSupplier: boolean) {
		if (this.auth.testGame) {
			if (isSupplier) {
				if(game.TagCodes && game.TagCodes.includes('is-coming-soon')) {
					return;
				}
	
				if (!game.IsMaintenance) {
					if (this.providerWithCustomLobby.includes(game.ProviderCode)) {
						const url = `/#/contents/pages/game-lobby?providerId=${game.ProviderId}&isTestGame=true`;
						window.open(url, '_blank');
						return;
					} else if (!this.providerWithCustomLobby.includes(game.ProviderCode)) {
						const url = `/#/contents/pages/loading-page?providerCode=${game.ProviderCode}&gameCode=${game.GameCode}&isTestGame=true`;
						window.open(url, '_blank');
						return;
					}
				}
			} else {
				if (!game.IsMaintenance) {
					const url = `/#/contents/pages/loading-page?providerCode=${game.ProviderCode}&gameCode=${game.GameCode}&isTestGame=true`;
					window.open(url, '_blank');
					return;
				}
			}
		}

		if (!this.auth.isAuthenticated()) {
			this.auth.toggleLoginDisplay(!this.auth.isAuthenticated());
			return;
		}

		if (isSupplier) {
			if(game.TagCodes && game.TagCodes.includes('is-coming-soon')) {
				return;
			}

			if (!game.IsMaintenance) {
				if (this.providerWithCustomLobby.includes(game.ProviderCode)) {
					const url = `/#/contents/pages/game-lobby?providerId=${game.ProviderId}&isTestGame=false`;
					window.open(url, '_blank');
				} else if (!this.providerWithCustomLobby.includes(game.ProviderCode)) {
					const url = `/#/contents/pages/loading-page?providerCode=${game.ProviderCode}&gameCode=${game.GameCode}&isTestGame=false`;
					window.open(url, '_blank');
				}
			} 			
		} else {
			if (!game.IsMaintenance) {
				const url = `/#/contents/pages/loading-page?providerCode=${game.ProviderCode}&gameCode=${game.GameCode}&isTestGame=false`;
				window.open(url, '_blank');
			}
		}
	}
}
